import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { HiOutlineChevronRight, HiOutlineMenu } from "react-icons/hi";
import { toast } from "sonner";

import DashboardLayout from "layouts/dashboard/DashboardLayout";
import {
  getRenewalDiscussionDetails,
  RenewalWorkflowStep,
  skipRenewalStep,
} from "api/renewal";
import InactiveStepView from "./InactiveStepView";
import RenewalPreliminaryDiscussionView, {
  RENEWAL_PRELIMINARY_FORM_ID,
} from "./RenewalPreliminaryDiscussionView";
import RenewalApplicationDetailsFromClientView, {
  RENEWAL_APPLICATION_DETAILS_FORM_ID,
} from "./RenewalApplicationDetailsFromClientView";
import RenewalInvoiceToClientView, {
  RENEWAL_INVOICE_TO_CLIENT_FORM_ID,
} from "./RenewalInvoiceToClientView";
import RenewalPaymentFromClientView, {
  RENEWAL_PAYMENT_FROM_CLIENT_FORM_ID,
} from "./RenewalPaymentFromClientView";
import RenewalStampPaperDetailsToVendorView, {
  RENEWAL_STAMP_PAPER_DETAILS_TO_VENDOR_FORM_ID,
} from "./RenewalStampPaperDetailsToVendorView";
import RenewalStampPaperFromVendorView, {
  RENEWAL_STAMP_PAPER_FROM_VENDOR_FORM_ID,
} from "./RenewalStampPaperFromVendorView";
import RenewalFileFormsView, {
  RENEWAL_FILE_FORMS_FORM_ID,
} from "./RenewalFileFormsView";
import RenewalPreparationOfPowerOfAttorneyView, {
  RENEWAL_PREPARATION_OF_POA_FORM_ID,
} from "./RenewalPreparationOfPowerOfAttorneyView";
import RenewalInformClientOfFilingView, {
  RENEWAL_INFORM_CLIENT_OF_FILING_FORM_ID,
} from "./RenewalInformClientOfFilingView";
import RenewalNoticeToClientView, {
  RENEWAL_NOTICE_TO_CLIENT_FORM_ID,
} from "./RenewalNoticeToClientView";
import RenewalStatusDocumentsView, {
  RENEWAL_STATUS_DOCUMENTS_FORM_ID,
} from "./RenewalStatusDocumentsView";
import RenewalInformClientAboutRenewalView, {
  RENEWAL_INFORM_CLIENT_ABOUT_RENEWAL_FORM_ID,
} from "./RenewalInformClientAboutRenewalView";

// ---------------------------------------------------------------------------
// Step maps
// ---------------------------------------------------------------------------

type Section = "discussion" | "filing" | "status";

const DISCUSSION_ITEMS: { id: string; label: string }[] = [
  { id: "preliminary", label: "Preliminary Discussion with the Client" },
  { id: "application-details", label: "Application Details from Client" },
  { id: "invoice", label: "Invoice to Client" },
  { id: "payment", label: "Payment from Client" },
];

const DISCUSSION_ID_TO_SLUG: Record<string, string> = {
  preliminary: "preliminary-discussion-with-the-client",
  "application-details": "application-details-from-client",
  invoice: "invoice-to-client",
  payment: "payment-from-client-for-renewal",
};

const DISCUSSION_SLUG_TO_ID: Record<string, string> = Object.fromEntries(
  Object.entries(DISCUSSION_ID_TO_SLUG).map(([k, v]) => [v, k])
);

const FILING_ITEMS: { id: string; label: string }[] = [
  { id: "stamp-paper-details-to-vendor", label: "Stamp Paper details to Vendor" },
  { id: "stamp-paper-from-vendor", label: "Stamp Paper from Vendor" },
  { id: "preparation-of-power-of-attorney", label: "Preparation of Power of Attorney" },
  { id: "file-forms", label: "File forms" },
  { id: "inform-client-of-filing", label: "Inform Client about Filing" },
];

const FILING_ID_TO_SLUG: Record<string, string> = {
  "stamp-paper-details-to-vendor": "stamp-paper-details-to-vendor",
  "stamp-paper-from-vendor": "stamp-paper-from-vendor",
  "preparation-of-power-of-attorney": "preparation-of-power-of-attorney",
  "file-forms": "file-forms",
  "inform-client-of-filing": "inform-client-of-filing",
};

const FILING_SLUG_TO_ID: Record<string, string> = Object.fromEntries(
  Object.entries(FILING_ID_TO_SLUG).map(([k, v]) => [v, k])
);

const STATUS_ITEMS: { id: string; label: string }[] = [
  { id: "download-status", label: "Download Status Documents from Trademarks Registry" },
  { id: "notice-to-client", label: "Notice to Client" },
  { id: "inform-client", label: "Inform Client" },
];

const STATUS_ID_TO_SLUG: Record<string, string> = {
  "download-status": "download-status-documents",
  "notice-to-client": "notice-to-client-of-renewal",
  "inform-client": "inform-client-about-renewal",
};

const STATUS_SLUG_TO_ID: Record<string, string> = Object.fromEntries(
  Object.entries(STATUS_ID_TO_SLUG).map(([k, v]) => [v, k])
);

const FORM_MAP: Record<string, string | undefined> = {
  preliminary: RENEWAL_PRELIMINARY_FORM_ID,
  "application-details": RENEWAL_APPLICATION_DETAILS_FORM_ID,
  invoice: RENEWAL_INVOICE_TO_CLIENT_FORM_ID,
  payment: RENEWAL_PAYMENT_FROM_CLIENT_FORM_ID,
  "stamp-paper-details-to-vendor":
    RENEWAL_STAMP_PAPER_DETAILS_TO_VENDOR_FORM_ID,
  "stamp-paper-from-vendor": RENEWAL_STAMP_PAPER_FROM_VENDOR_FORM_ID,
  "preparation-of-power-of-attorney": RENEWAL_PREPARATION_OF_POA_FORM_ID,
  "file-forms": RENEWAL_FILE_FORMS_FORM_ID,
  "inform-client-of-filing": RENEWAL_INFORM_CLIENT_OF_FILING_FORM_ID,
  "download-status": RENEWAL_STATUS_DOCUMENTS_FORM_ID,
  "notice-to-client": RENEWAL_NOTICE_TO_CLIENT_FORM_ID,
  "inform-client": RENEWAL_INFORM_CLIENT_ABOUT_RENEWAL_FORM_ID,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function DashboardRenewal() {
  const navigate = useNavigate();
  const { section, step, leadId } = useParams<{
    section?: string;
    step?: string;
    leadId?: string;
  }>();

  const [menuOpen, setMenuOpen] = useState(false);
  const [markNameForHeader, setMarkNameForHeader] = useState<string | null>(null);
  const [invoiceSent, setInvoiceSent] = useState(false);
  const [stampPaperSent, setStampPaperSent] = useState(false);
  const [informClientFilingSent, setInformClientFilingSent] = useState(false);
  const [noticeToClientSent, setNoticeToClientSent] = useState(false);
  const [informClientAboutRenewalSent, setInformClientAboutRenewalSent] =
    useState(false);
  const [progressing, setProgressing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!leadId) {
      setMarkNameForHeader(null);
      return;
    }
    let cancelled = false;
    getRenewalDiscussionDetails(leadId)
      .then((details) => {
        if (!cancelled) setMarkNameForHeader(details.preliminary.markName ?? null);
      })
      .catch(() => {
        if (!cancelled) setMarkNameForHeader(null);
      });
    return () => { cancelled = true; };
  }, [leadId]);

  const activeTab: Section =
    section === "filing" ? "filing" : section === "status" ? "status" : "discussion";

  const activeMenuItem =
    activeTab === "discussion" && step
      ? (DISCUSSION_SLUG_TO_ID[step] ?? "preliminary")
      : activeTab === "filing" && step
        ? (FILING_SLUG_TO_ID[step] ?? "stamp-paper-details-to-vendor")
        : activeTab === "status" && step
          ? (STATUS_SLUG_TO_ID[step] ?? "download-status")
          : activeTab === "filing"
            ? "stamp-paper-details-to-vendor"
            : activeTab === "status"
              ? "download-status"
              : "preliminary";

  useEffect(() => {
    if (!section || !step) {
      navigate(
        "/dashboard/renewal/new/discussion/preliminary-discussion-with-the-client",
        { replace: true }
      );
    }
  }, [section, step, navigate]);

  const navigateToStep = (
    tab: Section,
    itemId: string,
    currentLeadId?: string | null
  ) => {
    const slug =
      tab === "discussion"
        ? DISCUSSION_ID_TO_SLUG[itemId]
        : tab === "filing"
          ? FILING_ID_TO_SLUG[itemId]
          : STATUS_ID_TO_SLUG[itemId];
    if (!slug) return;
    const base = `/dashboard/renewal/new/${tab}/${slug}`;
    const path = currentLeadId ? `${base}/${currentLeadId}` : base;
    navigate(path);
    setMenuOpen(false);
  };

  const handleTabChange = (tab: Section) => {
    const firstStep =
      tab === "filing"
        ? "stamp-paper-details-to-vendor"
        : tab === "status"
          ? "download-status"
          : "preliminary";
    const slug =
      tab === "filing"
        ? FILING_ID_TO_SLUG[firstStep]
        : tab === "status"
          ? STATUS_ID_TO_SLUG[firstStep]
          : DISCUSSION_ID_TO_SLUG[firstStep];
    const path = leadId
      ? `/dashboard/renewal/new/${tab}/${slug}/${leadId}`
      : `/dashboard/renewal/new/${tab}/${slug}`;
    navigate(path);
  };

  const leftPanelItems =
    activeTab === "filing"
      ? FILING_ITEMS
      : activeTab === "status"
        ? STATUS_ITEMS
        : DISCUSSION_ITEMS;

  const activeLeftId = activeMenuItem;
  const activeIdx = leftPanelItems.findIndex((m) => m.id === activeLeftId);
  const canGoBack = activeIdx > 0;

  const submitForm = FORM_MAP[activeMenuItem] ?? null;
  const isInvoiceStep = activeTab === "discussion" && activeMenuItem === "invoice";
  const isStampPaperStep =
    activeTab === "filing" && activeMenuItem === "stamp-paper-details-to-vendor";
  const isInformClientOfFilingStep =
    activeTab === "filing" && activeMenuItem === "inform-client-of-filing";
  const isNoticeToClientStep =
    activeTab === "status" && activeMenuItem === "notice-to-client";
  const isInformClientAboutRenewalStep =
    activeTab === "status" && activeMenuItem === "inform-client";
  const primaryActionLabel =
    (isInvoiceStep && !invoiceSent) ||
    (isStampPaperStep && !stampPaperSent) ||
    (isInformClientOfFilingStep && !informClientFilingSent) ||
    (isNoticeToClientStep && !noticeToClientSent) ||
    (isInformClientAboutRenewalStep && !informClientAboutRenewalSent)
      ? "Send & Continue"
      : "Save & Continue";

  // Progress (skip) button: shown on the Payment step.
  const progressStep =
    activeTab === "discussion" && activeMenuItem === "payment"
      ? { step: RenewalWorkflowStep.RN_S1_04_PaymentFromClient }
      : null;

  const handleProgress = async () => {
    if (!progressStep || !leadId || progressing) return;
    setProgressing(true);
    try {
      await skipRenewalStep(leadId, progressStep.step);
      toast.success("Step marked as skipped.");
      navigateToStep("filing", "stamp-paper-details-to-vendor", leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to skip step");
    } finally {
      setProgressing(false);
    }
  };

  return (
    <DashboardLayout headerTitle="Renewal" headerLeadNo={markNameForHeader ?? undefined} hideHeaderOnMobile>
      <div className="flex-1 p-2 min-h-0 flex flex-col min-w-0">
        <div className="bg-white rounded-[8px] h-full flex flex-col overflow-hidden">
          {/* Tab bar */}
          <div className="flex items-center gap-0 border-b border-[#EEEEEE] pl-[10px] pt-[6px] flex-shrink-0">
            {(["discussion", "filing", "status"] as Section[]).map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`px-2.5 py-2.5 text-[14px] leading-5 -mb-px border-b-2 transition-colors capitalize ${
                  activeTab === tab
                    ? "font-semibold text-[#222] border-[#222]"
                    : "font-normal text-[#222] border-transparent"
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Mobile header */}
          <div className="md:hidden flex items-center gap-3 h-[65px] px-4 border-b border-[#E5E7EB] flex-shrink-0">
            <button
              type="button"
              aria-label="Open menu"
              onClick={() => setMenuOpen(true)}
              className="flex items-center justify-center rounded-[10px] w-9 h-9 min-w-[36px] min-h-[36px] hover:bg-[#F7F7F7]"
            >
              <HiOutlineMenu className="w-5 h-5 text-[#222]" strokeWidth={1.5} />
            </button>
            <div className="flex-1 min-w-0 font-medium text-[14px] leading-5 text-[#242424] truncate">
              Renewal{markNameForHeader ? ` | ${markNameForHeader}` : ""}
            </div>
          </div>

          <div className="flex flex-1 overflow-hidden relative">
            {/* Mobile overlay */}
            {menuOpen && (
              <div
                className="absolute inset-0 bg-black/20 z-40 md:hidden"
                onClick={() => setMenuOpen(false)}
              />
            )}

            {/* Left panel */}
            <div
              className={`w-[279px] border-r border-[#EEEEEE] bg-white p-2.5 overflow-y-auto flex-shrink-0 ${
                menuOpen
                  ? "absolute left-0 top-0 bottom-0 z-50 md:relative"
                  : "hidden md:block"
              }`}
            >
              <div className="flex flex-col gap-2">
                {leftPanelItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => navigateToStep(activeTab, item.id, leadId ?? null)}
                    className={`w-full flex items-center justify-between px-2.5 py-2.5 gap-2.5 rounded-[6px] text-xs ${
                      activeLeftId === item.id
                        ? "bg-[#222222] text-white font-medium"
                        : "bg-[#F7F7F7] text-[#222222] font-normal"
                    } hover:bg-opacity-90 transition-colors`}
                  >
                    <span className="text-left line-clamp-1">{item.label}</span>
                    <HiOutlineChevronRight className="w-4 h-4 flex-shrink-0 ml-1" strokeWidth={1.5} />
                  </button>
                ))}
              </div>
            </div>

            {/* Main content */}
            <div className="flex-1 flex flex-col min-w-0">
              <div className="flex-1 p-5 md:p-6 overflow-y-auto">
                {activeTab === "discussion" && activeMenuItem === "preliminary" ? (
                  <RenewalPreliminaryDiscussionView
                    leadId={leadId ?? undefined}
                    onSubmittingChange={setSubmitting}
                    onSuccess={(newLeadId) => {
                      navigateToStep("discussion", "application-details", newLeadId);
                    }}
                  />
                ) : activeTab === "discussion" && activeMenuItem === "application-details" ? (
                  <RenewalApplicationDetailsFromClientView
                    leadId={leadId ?? null}
                    onSubmittingChange={setSubmitting}
                    onSuccess={(currentLeadId) => {
                      navigateToStep("discussion", "invoice", currentLeadId);
                    }}
                  />
                ) : activeTab === "discussion" && activeMenuItem === "invoice" ? (
                  <RenewalInvoiceToClientView
                    leadId={leadId ?? null}
                    onSentStatusChange={setInvoiceSent}
                    onSuccess={(currentLeadId) => {
                      navigateToStep("discussion", "payment", currentLeadId);
                    }}
                  />
                ) : activeTab === "discussion" && activeMenuItem === "payment" ? (
                  <RenewalPaymentFromClientView
                    leadId={leadId ?? null}
                    onSubmittingChange={setSubmitting}
                    onSuccess={(currentLeadId) => {
                      navigateToStep("filing", "stamp-paper-details-to-vendor", currentLeadId);
                    }}
                  />
                ) : activeTab === "filing" && activeMenuItem === "stamp-paper-details-to-vendor" ? (
                  <RenewalStampPaperDetailsToVendorView
                    leadId={leadId ?? null}
                    onSentStatusChange={setStampPaperSent}
                    onSuccess={(currentLeadId) => {
                      navigateToStep(
                        "filing",
                        "stamp-paper-from-vendor",
                        currentLeadId
                      );
                    }}
                  />
                ) : activeTab === "filing" && activeMenuItem === "stamp-paper-from-vendor" ? (
                  <RenewalStampPaperFromVendorView
                    leadId={leadId ?? null}
                    onSubmittingChange={setSubmitting}
                    onSuccess={(currentLeadId) => {
                      navigateToStep(
                        "filing",
                        "preparation-of-power-of-attorney",
                        currentLeadId
                      );
                    }}
                  />
                ) : activeTab === "filing" && activeMenuItem === "preparation-of-power-of-attorney" ? (
                  <RenewalPreparationOfPowerOfAttorneyView
                    leadId={leadId ?? null}
                    onSuccess={(currentLeadId) => {
                      navigateToStep("filing", "file-forms", currentLeadId);
                    }}
                  />
                ) : activeTab === "filing" && activeMenuItem === "file-forms" ? (
                  <RenewalFileFormsView
                    leadId={leadId ?? null}
                    onSuccess={(currentLeadId) => {
                      navigateToStep("filing", "inform-client-of-filing", currentLeadId);
                    }}
                  />
                ) : activeTab === "filing" && activeMenuItem === "inform-client-of-filing" ? (
                  <RenewalInformClientOfFilingView
                    leadId={leadId ?? null}
                    onSentStatusChange={setInformClientFilingSent}
                    onSuccess={(currentLeadId) => {
                      navigateToStep("status", "download-status", currentLeadId);
                    }}
                  />
                ) : activeTab === "status" && activeMenuItem === "download-status" ? (
                  <RenewalStatusDocumentsView
                    leadId={leadId ?? null}
                    onSuccess={(currentLeadId) => {
                      navigateToStep("status", "notice-to-client", currentLeadId);
                    }}
                  />
                ) : activeTab === "status" && activeMenuItem === "notice-to-client" ? (
                  <RenewalNoticeToClientView
                    leadId={leadId ?? null}
                    onSentStatusChange={setNoticeToClientSent}
                    onSuccess={(currentLeadId) => {
                      navigateToStep("status", "inform-client", currentLeadId);
                    }}
                  />
                ) : activeTab === "status" && activeMenuItem === "inform-client" ? (
                  <RenewalInformClientAboutRenewalView
                    leadId={leadId ?? null}
                    onSentStatusChange={setInformClientAboutRenewalSent}
                    onSuccess={() => {
                      // Final step of Renewal — stay on this step after sending.
                    }}
                  />
                ) : (
                  <InactiveStepView
                    title="Step coming soon"
                    body="This step has not been implemented yet. Please continue with the available steps."
                    buttonText="Back to Preliminary Discussion"
                    onButtonClick={() =>
                      navigateToStep("discussion", "preliminary", leadId ?? null)
                    }
                  />
                )}
              </div>

              {/* Footer */}
              <div className="border-t border-[#EEE] p-3 flex items-center justify-between gap-2 flex-shrink-0">
                <button
                  type="button"
                  disabled={!canGoBack}
                  onClick={() => {
                    if (!canGoBack) return;
                    navigateToStep(
                      activeTab,
                      leftPanelItems[activeIdx - 1].id,
                      leadId ?? null
                    );
                  }}
                  className="px-4 py-2 h-9 text-[12px] font-semibold text-[#6D6D6D] border border-[#EEE] rounded-[6px] bg-white hover:bg-[#F7F7F7] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Back
                </button>
                <div className="flex items-center gap-2 sm:gap-3 md:gap-5">
                  <button
                    type="button"
                    onClick={() => navigate("/dashboard/renewal")}
                    className="px-4 py-2 h-9 text-[12px] font-semibold text-[#6D6D6D] border border-[#EEE] rounded-[6px] bg-white hover:bg-[#F7F7F7]"
                  >
                    Close
                  </button>
                  {progressStep && (
                    <button
                      type="button"
                      onClick={handleProgress}
                      disabled={progressing}
                      className="px-4 py-2 h-9 text-[12px] font-semibold text-[#222] border border-[#222] rounded-[6px] bg-white hover:bg-[#F7F7F7] disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {progressing ? "Progressing…" : "Progress"}
                    </button>
                  )}
                  <button
                    type={submitForm ? "submit" : "button"}
                    form={submitForm ?? undefined}
                    disabled={!submitForm || submitting}
                    className="px-4 py-2 h-9 min-w-[128px] text-[12px] font-semibold text-white rounded-[6px] bg-[#222] hover:bg-[#333] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Saving…" : primaryActionLabel}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
